import { GetServerSidePropsContext } from 'next';

import {
  getSeoMetaAssets,
  getActorsName,
  replaceMetaData
} from 'src/components/pageMeta/SEOMetaUtils';
import { TvShowRelatedContent } from 'src/services/GraphQL/Queries/types';

import SimpleStore from '../../store/SimpleStore';
import { DynamicObject } from '../commonTypes';
import { HTTP_URL_PARAMS } from '../constants/HttpMethodsConstants';
import {
  ConsumptionPageUrls,
  FOOTER_URLS_CONSTANTS
} from '../constants/stringConstants';
import {
  DetailsPageSeoConstants,
  EpisodeSeoPropsGenerator,
  SeoPropsGeneratorTypes
} from '../constants/TvShowDetailsPageConstants';

const getPathNameFromContextUrl = (url: string) => {
  const urlSplit = url.split('/');
  let index = 1;
  if (urlSplit[index] === 'global') {
    index++;
  }
  if (urlSplit[index] === 'add-ons') {
    index += 2;
  }
  return urlSplit[index];
};

export const detailsPageType = (context: GetServerSidePropsContext) => {
  const pathName = getPathNameFromContextUrl(
    (context && context?.resolvedUrl) || ''
  );
  switch (pathName) {
    case FOOTER_URLS_CONSTANTS.KID:
      return FOOTER_URLS_CONSTANTS.KID;
    case FOOTER_URLS_CONSTANTS.TV_SHOWS:
      return FOOTER_URLS_CONSTANTS.TV_SHOWS;
    case FOOTER_URLS_CONSTANTS.WEB_SERIES:
      return FOOTER_URLS_CONSTANTS.WEB_SERIES;
    default:
      return '';
  }
};

export const generateBreadCrumbs = (
  context: GetServerSidePropsContext,
  episodeStrings: DynamicObject,
  detailsData: TvShowRelatedContent
) => {
  const pageType = detailsPageType(context);

  if (pageType === FOOTER_URLS_CONSTANTS.KIDS_SHOWS) {
    return [];
  }
  const isTvShows = pageType === FOOTER_URLS_CONSTANTS.TV_SHOWS;
  const labelValue = isTvShows
    ? episodeStrings?.BREADCRUMB?.TVSHOWS
    : episodeStrings?.BREADCRUMB?.WEBSERIES;
  const footerUrl = `/${pageType}`;
  const basePageUrls = isTvShows
    ? ConsumptionPageUrls.TVSHOWS_CONSUMPTION
    : ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION;

  return [
    {
      label: labelValue,
      url: footerUrl
    },
    {
      label: detailsData.title || detailsData.originalTitle,
      url: `/${basePageUrls}/${context?.query?.showName}/${context?.query?.showId}`
    }
  ];
};

export const generateBreadCrumbsForEpisodePage = (
  router: GetServerSidePropsContext,
  episodeStrings: DynamicObject,
  tvShowDetails: TvShowRelatedContent
) => {
  const pageType = detailsPageType(router);
  if (pageType === FOOTER_URLS_CONSTANTS.KID) {
    return [];
  }
  const isTvShows = pageType === FOOTER_URLS_CONSTANTS.TV_SHOWS;
  const labelValue = isTvShows
    ? episodeStrings?.BREADCRUMB?.TVSHOWS
    : episodeStrings?.BREADCRUMB?.WEBSERIES;
  const pathName = router?.resolvedUrl?.split('/');
  return [
    {
      url: '/',
      label: episodeStrings?.BREADCRUMB?.HOME
    },
    {
      url: router?.resolvedUrl?.includes('season-')
        ? pathName?.slice(0, -5)?.join('/') || ''
        : pathName?.slice(0, -4)?.join('/') || '',
      label: labelValue
    },
    {
      url: router?.resolvedUrl?.includes('season-')
        ? pathName?.slice(0, -2)?.join('/') || ''
        : pathName?.slice(0, -1)?.join('/') || '',
      label: (tvShowDetails?.title
        ? tvShowDetails?.title
        : tvShowDetails?.originalTitle) as string
    },
    {
      url: '',
      label: episodeStrings?.BREADCRUMB?.EPISODES
    }
  ];
};

export const seoPropsGenerator = async ({
  detailsData,
  episodeStrings,
  licenseData,
  context,
  allDisplayLanguages = []
}: SeoPropsGeneratorTypes) => {
  if (detailsData) {
    const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
    const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
    let metaUrl = '';
    const regExpOrig = new RegExp(`${FOOTER_URLS_CONSTANTS.WEB_SERIES}`, 'g');
    const regExpKids = new RegExp(`${FOOTER_URLS_CONSTANTS.KIDS_SHOWS}`, 'g');
    if (context?.req?.url?.match(regExpOrig)) {
      metaUrl = `/${ConsumptionPageUrls.ZEE5ORIGINALSCONSUMPTION}`;
    } else if (context?.req?.url?.match(regExpKids)) {
      metaUrl = `/${ConsumptionPageUrls.KIDS_CONSUMPTION_SHOWS}`;
    } else {
      metaUrl = `/${ConsumptionPageUrls.TVSHOWS_CONSUMPTION}`;
    }
    const metaDataType: DynamicObject = {};
    const seasons = ((detailsData?.seasons &&
      detailsData?.seasons[0]?.originalTitle) ||
      '') as string;
    const showName = (detailsData?.originalTitle ||
      detailsData?.title ||
      '') as string;
    const actors = getActorsName((detailsData?.actors as string[]) || []);
    const episodeCount =
      (detailsData?.seasons &&
        detailsData?.seasons?.reduce<number>((accumulator: number, item) => {
          return accumulator + (item?.totalEpisodes ? item?.totalEpisodes : 0);
        }, 0)) ||
      0;
    let seoMetaDescription = detailsData?.extended?.seoDescription || '';
    let seoTitle = detailsData?.extended?.seoTitle || '';
    if (seoMetaDescription === '' || seoTitle === '') {
      const seoMetaAssets: DynamicObject = await getSeoMetaAssets(
        countryCode,
        displayLanguage
      );
      const staticMetaData = seoMetaAssets[metaUrl];
      seoMetaDescription = staticMetaData?.description;
      seoTitle = staticMetaData?.title;
    }
    metaDataType.title = seoTitle;
    metaDataType.description = seoMetaDescription;
    const replaceFromArray = [
      '<TV Show Name>',
      '<Show Name>',
      '<Season No>',
      '<Star Cast Names>',
      '<Episode Count>'
    ];
    const replaceToArray = [
      showName,
      showName,
      seasons,
      actors,
      episodeCount?.toString()
    ];
    const contentPartnerName = detailsData.contentPartnerDetails?.name || '';
    const metaData = replaceMetaData({
      metaDataObj: metaDataType,
      languages: allDisplayLanguages || [],
      languageCode: displayLanguage,
      replaceFromArray: replaceFromArray,
      replaceToArray: replaceToArray,
      contentPartnerName,
      countryCode
    });

    const breadCrumbItems =
      generateBreadCrumbs(context, episodeStrings, detailsData) || [];
    return {
      pageMeta: metaData,
      searchSchemaData: true,
      apiData: detailsData,
      breadcrumbs: breadCrumbItems,
      itemListSchema: detailsData,
      isConsumption: true,
      railData:
        (detailsData?.seasons && detailsData.seasons[0]?.episodes) || [],
      seasonIndex:
        (Object.keys(detailsData).length &&
          detailsData?.seasons?.[0]?.orderId) ||
        1,
      tableSchema: true,
      header: `${DetailsPageSeoConstants.DETAILS_ABOUT} ${
        detailsData?.originalTitle || detailsData?.title
      } ${DetailsPageSeoConstants.SHOW} :`,
      consumptionData: detailsData,
      liteData: detailsData,
      tvSeriesSchemaShowDetailData: detailsData,
      tvEpisodeSchemaData: detailsData,
      licenseData: licenseData,
      keywords: detailsData?.extended?.metaKeywords,
      canonicalUrl: detailsData?.extended?.canonicalUrl
    };
  }
};

export const episodePageSeoPropsGenerator = async ({
  detailsData,
  allDisplayLanguages,
  activeSeasonIndex,
  breadCrumbItems,
  licenseData
}: EpisodeSeoPropsGenerator) => {
  const countryCode = SimpleStore.get(HTTP_URL_PARAMS.COUNTRY_CODE);
  const displayLanguage = SimpleStore.get(HTTP_URL_PARAMS.DISPLAY_LANGUAGE);
  const SEO_URL_TV_SHOW_EPISODES = '/tv-shows/episodes';
  const seoMetaAssets: DynamicObject = await getSeoMetaAssets(
    countryCode,
    displayLanguage
  );
  const staticMetaData = seoMetaAssets[SEO_URL_TV_SHOW_EPISODES];
  const metaDataType: DynamicObject = {};
  metaDataType.title =
    detailsData?.extended?.seoTitle || staticMetaData?.title || '';
  metaDataType.description =
    detailsData?.extended?.seoDescription || staticMetaData?.description || '';
  const seasonTitle = (
    detailsData?.title ? detailsData?.title : detailsData?.originalTitle || ''
  ) as string;
  const actors = getActorsName((detailsData?.actors as string[]) || []);
  const replaceFromArray = [
    '<TV Show Name>',
    '<Show Name>',
    '<Season No>',
    '<Star Cast Names>'
  ];
  const seasonNumber = '0';
  const replaceToArray = [seasonTitle, seasonTitle, seasonNumber, actors];
  const metaData = replaceMetaData({
    metaDataObj: metaDataType,
    languages: allDisplayLanguages,
    languageCode: displayLanguage,
    replaceFromArray: replaceFromArray,
    replaceToArray: replaceToArray
  });
  return {
    pageMeta: metaData,
    apiData:
      detailsData?.seasons && detailsData?.seasons[activeSeasonIndex]?.episodes,
    breadcrumbs: breadCrumbItems,
    itemListSchema: detailsData,
    isConsumption: false,
    railData:
      detailsData?.seasons && detailsData?.seasons[activeSeasonIndex]?.episodes,
    seasonIndex: activeSeasonIndex,
    licenseData: licenseData
  };
};
